// ----------------------------------------------------------------------------
// offline-render — Offline bounce-to-WAV for DAWSEUR
//
// Renders the full song or loop section offline using OfflineAudioContext,
// including all track FX, EQ, fades, time-stretch, and automations.
// Shows a progress bar during rendering.
// ----------------------------------------------------------------------------

const afx = require('../../common/afx.js');
const fxs = require('../../common/audiofx.js');

// SoundTouch for time-stretching (ESM module imported via esbuild)
const { SoundTouch, SimpleFilter, WebAudioBufferSource } = require('soundtouchjs');

// ---- Trim leading silence caused by ScriptProcessorNode latency ----
// Each ScriptProcessorNode-based FX adds one quantum (bufferSize=4096 ≈ 93ms
// at 44100Hz) of latency at the start of the rendered buffer. With multiple FX
// chained, this accumulates into audible padding. We detect and trim it.
function trimLeadingSilence(buffer, ctx, threshold) {
   threshold = threshold || 0.0005; // ~-66dB
   const numChannels = buffer.numberOfChannels;
   const numFrames = buffer.length;
   const sampleRate = buffer.sampleRate;
   const channels = [];
   for (let c = 0; c < numChannels; c++) {
      channels.push(buffer.getChannelData(c));
   }

   // Find first sample where any channel exceeds threshold
   let firstNonSilent = -1;
   for (let i = 0; i < numFrames; i++) {
      let loud = false;
      for (let c = 0; c < numChannels; c++) {
         if (Math.abs(channels[c][i]) > threshold) { loud = true; break; }
      }
      if (loud) { firstNonSilent = i; break; }
   }

   if (firstNonSilent <= 0) return buffer; // nothing to trim

   // Cap trimming at 2 seconds to avoid removing intentional musical silence
   const maxTrimSamples = 2 * sampleRate;
   if (firstNonSilent > maxTrimSamples) firstNonSilent = maxTrimSamples;

   // Also keep a tiny fade-in (64 samples) to avoid a click at the start
   const fadeSamples = Math.min(64, firstNonSilent);
   const trimmedFrames = numFrames - firstNonSilent;
   const newBuffer = ctx.createBuffer(numChannels, trimmedFrames, sampleRate);
   for (let c = 0; c < numChannels; c++) {
      const src = channels[c];
      const dst = newBuffer.getChannelData(c);
      dst.set(src.subarray(firstNonSilent), 0);
      // Apply short fade-in to prevent click
      for (let i = 0; i < fadeSamples && i < trimmedFrames; i++) {
         dst[i] *= i / fadeSamples;
      }
   }
   return newBuffer;
}

// ---- WAV encoder (16-bit PCM) ----
function audioBufferToWav(buffer) {
   const numChannels = buffer.numberOfChannels;
   const sampleRate = buffer.sampleRate;
   const numFrames = buffer.length;
   const bytesPerSample = 2;
   const dataSize = numFrames * numChannels * bytesPerSample;
   const headerSize = 44;
   const totalSize = headerSize + dataSize;

   const arr = new ArrayBuffer(totalSize);
   const view = new DataView(arr);

   // RIFF header
   writeString(view, 0, 'RIFF');
   view.setUint32(4, totalSize - 8, true);
   writeString(view, 8, 'WAVE');

   // fmt chunk
   writeString(view, 12, 'fmt ');
   view.setUint32(16, 16, true); // chunk size
   view.setUint16(20, 1, true);  // PCM format
   view.setUint16(22, numChannels, true);
   view.setUint32(24, sampleRate, true);
   view.setUint32(28, sampleRate * numChannels * bytesPerSample, true);
   view.setUint16(32, numChannels * bytesPerSample, true);
   view.setUint16(34, 16, true); // bits per sample

   // data chunk
   writeString(view, 36, 'data');
   view.setUint32(40, dataSize, true);

   // Interleave and write samples
   const channels = [];
   for (let c = 0; c < numChannels; c++) {
      channels.push(buffer.getChannelData(c));
   }

   let offset = 44;
   for (let i = 0; i < numFrames; i++) {
      for (let c = 0; c < numChannels; c++) {
         let sample = channels[c][i];
         // Clamp and convert to 16-bit
         sample = Math.max(-1, Math.min(1, sample));
         view.setInt16(offset, sample < 0 ? sample * 0x8000 : sample * 0x7FFF, true);
         offset += 2;
      }
   }

   return new Blob([arr], { type: 'audio/wav' });
}

function writeString(view, offset, str) {
   for (let i = 0; i < str.length; i++) {
      view.setUint8(offset + i, str.charCodeAt(i));
   }
}

// ---- Main render function ----
// engine: the playback engine
// automation: the automation module
// opts: { loopOnly: bool, onProgress: fn(pct), sampleRate: number }
// Returns: Promise<Blob>
async function renderToWav(engine, automation, opts) {
   opts = opts || {};
   const onProgress = opts.onProgress || (() => {});
   const sampleRate = opts.sampleRate || 44100;

   const state = engine.state;
   const bpm = state.bpm;

   // Determine render range
   let startBeat = 0;
   let endBeat = 0;

   if (opts.loopOnly && state.loopEnabled && state.loopEnd > state.loopStart) {
      startBeat = state.loopStart;
      endBeat = state.loopEnd;
   } else {
      // Find the end of the song (latest event end)
      for (const track of state.tracks) {
         for (const ev of track.events) {
            const evEnd = ev.startBeat + ev.lengthBeats;
            if (evEnd > endBeat) endBeat = evEnd;
         }
      }
      // Round up to nearest bar (4 beats)
      if (endBeat > 0) {
         endBeat = Math.ceil(endBeat / 4) * 4;
      } else {
         endBeat = 4; // minimum 1 bar
      }
   }

   const totalBeats = endBeat - startBeat;
   const totalSeconds = (totalBeats / bpm) * 60;
   const totalSamples = Math.ceil(totalSeconds * sampleRate);
   const numChannels = 2;

   onProgress(0.02);

   // Create offline context
   const OfflineCtx = window.OfflineAudioContext || window.webkitOfflineAudioContext;
   const offCtx = new OfflineCtx(numChannels, totalSamples, sampleRate);

   // Collect ready promises from AudioWorklet-based FX — must be awaited
   // before startRendering() so the worklet nodes are connected
   const fxReadyPromises = [];

   // ---- Rebuild master chain ----
   const masterEqLow = offCtx.createBiquadFilter();
   masterEqLow.type = 'lowshelf';
   masterEqLow.frequency.value = 200;
   masterEqLow.gain.value = state.masterEqLowGain || 0;

   const masterEqMid = offCtx.createBiquadFilter();
   masterEqMid.type = 'peaking';
   masterEqMid.frequency.value = 1000;
   masterEqMid.Q.value = 1;
   masterEqMid.gain.value = state.masterEqMidGain || 0;

   const masterEqHi = offCtx.createBiquadFilter();
   masterEqHi.type = 'highshelf';
   masterEqHi.frequency.value = 5000;
   masterEqHi.gain.value = state.masterEqHiGain || 0;

   const masterPan = offCtx.createStereoPanner();
   masterPan.pan.value = state.masterPanVal || 0;

   const masterGain = offCtx.createGain();
   masterGain.gain.value = state.masterVolume !== undefined ? state.masterVolume : 1.0;

   // Master compressor/limiter — safety limiter (same settings as live engine)
   const masterComp = offCtx.createDynamicsCompressor();
   masterComp.threshold.value = state.masterCompThreshold !== undefined ? state.masterCompThreshold : -3;
   masterComp.knee.value = state.masterCompKnee !== undefined ? state.masterCompKnee : 0;
   masterComp.ratio.value = state.masterCompRatio !== undefined ? state.masterCompRatio : 20;
   masterComp.attack.value = state.masterCompAttack !== undefined ? state.masterCompAttack : 0.003;
   masterComp.release.value = state.masterCompRelease !== undefined ? state.masterCompRelease : 0.1;

   masterEqLow.connect(masterEqMid);
   masterEqMid.connect(masterEqHi);
   masterEqHi.connect(masterPan);
   masterPan.connect(masterGain);
   masterGain.connect(masterComp);
   masterComp.connect(offCtx.destination);

   onProgress(0.05);

   // ---- Rebuild tracks ----
   const offTracks = {};

   for (const track of state.tracks) {
      const offTrack = {};

      // Pre-FX gain
      offTrack.gainNode = offCtx.createGain();
      offTrack.gainNode.gain.value = track.muted ? 0 : (track.volume !== undefined ? track.volume : 0.8);

      // Pre-FX pan
      offTrack.pannerNode = offCtx.createStereoPanner();
      offTrack.pannerNode.pan.value = (track.pan !== undefined ? track.pan : 0) / 50;

      // FX chain
      offTrack.fxInput = offCtx.createGain();
      offTrack.fxOutput = offCtx.createGain();

      // EQ
      offTrack.eqLow = offCtx.createBiquadFilter();
      offTrack.eqLow.type = 'lowshelf';
      offTrack.eqLow.frequency.value = 200;
      offTrack.eqLow.gain.value = track.eqLowGain || 0;

      offTrack.eqMid = offCtx.createBiquadFilter();
      offTrack.eqMid.type = 'peaking';
      offTrack.eqMid.frequency.value = 1000;
      offTrack.eqMid.Q.value = 1;
      offTrack.eqMid.gain.value = track.eqMidGain || 0;

      offTrack.eqHi = offCtx.createBiquadFilter();
      offTrack.eqHi.type = 'highshelf';
      offTrack.eqHi.frequency.value = 5000;
      offTrack.eqHi.gain.value = track.eqHiGain || 0;

      // Mixer pan & gain (post-EQ)
      offTrack.mixerPan = offCtx.createStereoPanner();
      offTrack.mixerPan.pan.value = (track.mixerPanVal || 0) / 50;

      offTrack.mixerGain = offCtx.createGain();
      offTrack.mixerGain.gain.value = track.mixerVolume !== undefined ? track.mixerVolume : 0.8;

      // Connect: gainNode → pannerNode → fxInput → [FX] → fxOutput → eqLow → eqMid → eqHi → mixerPan → mixerGain → master
      offTrack.gainNode.connect(offTrack.pannerNode);
      offTrack.pannerNode.connect(offTrack.fxInput);

      // Build FX chain on offline context
      const activeFx = (track.fxChain || []).filter(fx => !fx._bypassed);
      offTrack.fxNodes = []; // exposed for FX parameter automation lookup
      if (activeFx.length > 0) {
         // Recreate each FX on the offline context
         const offFxNodes = [];
         for (const origFx of activeFx) {
            try {
               // Get the FX definition to find inputs
               const fxDef = origFx._def;
               if (!fxDef) continue;

               // The 'stretch' FX relies on a Web Worker for async audio processing,
               // which is fundamentally incompatible with OfflineAudioContext (the
               // worker cannot return processed blocks synchronously during the
               // offline render pass). Skip it to avoid silent output.
               // AudioWorklet-based FX are supported: their `ready` promise is awaited
               // before rendering starts (see fxReadyPromises below).
               if (fxDef.name === 'stretch') {
                  console.warn('[render] Skipping stretch FX (incompatible with offline render)');
                  continue;
               }

               // Create inputs object from current values of the original controls
               const offInputs = {};
               if (origFx._controls) {
                  for (const inputDef of fxDef.inputs) {
                     const origEl = origFx._controls[inputDef.name];
                     if (origEl) {
                        // Create a dummy element with the current value
                        const dummy = {
                           value: origEl.value,
                           min: origEl.min,
                           max: origEl.max,
                           step: origEl.step,
                           addEventListener: () => {},
                        };
                        offInputs[inputDef.name] = dummy;
                     }
                  }
               }
               const offFx = fxDef.fn(offCtx, offInputs);

               // Collect ready promise for AudioWorklet-based FX
               if (offFx.ready) {
                  fxReadyPromises.push(offFx.ready);
               }

               // ---- Set initial parameter values directly ----
               // The FX implementations set internal node values to 0 at creation
               // and rely on ScriptProcessorNode.onaudioprocess to update them.
               // In OfflineAudioContext the onaudioprocess timing does not align
               // with rendering quanta, so parameters stay at 0 → silent output.
               // Use the FX's `params` spec to set correct values immediately.
               if (offFx.params) {
                  for (const paramName of Object.keys(offFx.params)) {
                     const inputVal = offInputs[paramName];
                     if (inputVal === undefined) continue;
                     const paramSpec = offFx.params[paramName];
                     const targets = Array.isArray(paramSpec) ? paramSpec : [paramSpec];
                     for (const t of targets) {
                        // Skip targets with null node (e.g. AudioWorklet-based FX
                        // where the worklet module isn't loaded on the offline context)
                        if (!t || !t.node) continue;
                        try {
                           t.node.value = t.transform(inputVal.value);
                        } catch (e) {
                           console.warn('[render] Failed to set initial FX param:', paramName, e);
                        }
                     }
                  }
               }

               offFxNodes.push(offFx);
            } catch (e) {
               console.warn('[render] Failed to recreate FX:', e);
            }
         }

         offTrack.fxNodes = offFxNodes; // store for FX parameter automation

         if (offFxNodes.length > 0) {
            offTrack.fxInput.connect(offFxNodes[0].input);
            for (let i = 0; i < offFxNodes.length - 1; i++) {
               offFxNodes[i].output.connect(offFxNodes[i + 1].input);
            }
            offFxNodes[offFxNodes.length - 1].output.connect(offTrack.fxOutput);
         } else {
            offTrack.fxInput.connect(offTrack.fxOutput);
         }
      } else {
         offTrack.fxInput.connect(offTrack.fxOutput);
      }

      offTrack.fxOutput.connect(offTrack.eqLow);
      offTrack.eqLow.connect(offTrack.eqMid);
      offTrack.eqMid.connect(offTrack.eqHi);
      offTrack.eqHi.connect(offTrack.mixerPan);
      offTrack.mixerPan.connect(offTrack.mixerGain);
      offTrack.mixerGain.connect(masterEqLow);

      offTracks[track.id] = offTrack;
   }

   onProgress(0.10);

   // ---- Schedule events ----
   const beatsToSeconds = (beats, bpmVal) => (beats / bpmVal) * 60;

   let scheduledCount = 0;
   const totalEvents = state.tracks.reduce((sum, t) => sum + t.events.length, 0);

   for (const track of state.tracks) {
      if (track.muted) continue;
      const anySolo = state.tracks.some(t => t.solo);
      if (anySolo && !track.solo) continue;

      const offTrack = offTracks[track.id];
      if (!offTrack) continue;

      for (const event of track.events) {
         // ---- MIDI clip types ----
         if (event.type === 'stepseq' || event.type === 'slicer' || event.type === 'pianoroll') {
            let evStartBeat = event.startBeat;
            let evEndBeat = event.startBeat + event.lengthBeats;
            if (evEndBeat <= startBeat || evStartBeat >= endBeat) continue;
            if (evStartBeat < startBeat) evStartBeat = startBeat;
            if (evEndBeat > endBeat) evEndBeat = endBeat;

            const eventVol = event.volume !== undefined ? event.volume : 0.8;
            const secPerBeat = 60 / bpm;

            if (event.type === 'stepseq') {
               const seq = event.sequence;
               if (!seq || !seq.channels) continue;
               const stepsPerBeat = 4;
               // Loop the sequence to fill the clip length
               const seqLenBeats = seq.steps / stepsPerBeat;
               const clipLenBeats = evEndBeat - evStartBeat;
               const numLoops = Math.ceil(clipLenBeats / seqLenBeats);
               for (const ch of seq.channels) {
                  if (!ch.sampleId) continue;
                  const chBuffer = await loadMIDISample(ch.sampleId);
                  if (!chBuffer) continue;
                  for (let loop = 0; loop < numLoops; loop++) {
                     for (let s = 0; s < seq.steps && s < ch.steps.length; s++) {
                        const stepVal = ch.steps[s];
                        if (stepVal === false || stepVal === undefined || stepVal === null) continue;
                        const stepBeat = evStartBeat + loop * seqLenBeats + s / stepsPerBeat;
                        if (stepBeat >= evEndBeat) break;
                        const stepSec = beatsToSeconds(stepBeat - startBeat, bpm);
                        if (stepSec < 0 || stepSec > totalSeconds) continue;
                        const semitone = typeof stepVal === 'number' ? stepVal : 0;
                        const playbackRate = Math.pow(2, semitone / 12);
                        scheduleOfflineSample(offCtx, chBuffer, offTrack.gainNode, stepSec, ch.volume * eventVol, playbackRate, track.pitchShift || 0);
                     }
                  }
               }
            } else if (event.type === 'slicer') {
               const seq = event.sequence;
               if (!seq || !seq.slices || !seq.pattern) continue;
               const buffer = await loadMIDISample(seq.sampleId);
               if (!buffer) continue;
               const sampleRate = buffer.sampleRate;
               const stepsPerBeat = 4;
               for (let s = 0; s < seq.steps && s < seq.pattern.length; s++) {
                  const sliceIndices = seq.pattern[s];
                  if (!sliceIndices || sliceIndices.length === 0) continue;
                  const stepBeat = evStartBeat + s / stepsPerBeat;
                  if (stepBeat >= evEndBeat) break;
                  const stepSec = beatsToSeconds(stepBeat - startBeat, bpm);
                  for (const si of sliceIndices) {
                     if (si < 0 || si >= seq.slices.length) continue;
                     const slice = seq.slices[si];
                     const offset = slice.start / sampleRate;
                     const duration = (slice.end - slice.start) / sampleRate;
                     scheduleOfflineSample(offCtx, buffer, offTrack.gainNode, stepSec, eventVol, 1.0, track.pitchShift || 0, offset, duration);
                  }
               }
            } else if (event.type === 'pianoroll') {
               const pat = event.pattern;
               if (!pat || !pat.notes || !pat.sampleId) continue;
               const buffer = await loadMIDISample(pat.sampleId);
               if (!buffer) continue;
               const stepsPerBeat = 4;
               for (const note of pat.notes) {
                  const noteBeat = evStartBeat + note.startStep / stepsPerBeat;
                  if (noteBeat >= evEndBeat) break;
                  const noteSec = beatsToSeconds(noteBeat - startBeat, bpm);
                  const noteDurSec = (note.lengthSteps / stepsPerBeat) * secPerBeat;
                  const midiNote = note.midi !== undefined ? note.midi : (note.semitone !== undefined ? note.semitone + 60 : 60);
                  const playbackRate = Math.pow(2, (midiNote - 60) / 12);
                  const velocity = (note.velocity !== undefined ? note.velocity : 1.0) * eventVol;
                  if (pat.loopEnabled && pat.loopEnd > pat.loopStart) {
                     scheduleOfflineSampleLooped(offCtx, buffer, offTrack.gainNode, noteSec, velocity, playbackRate, track.pitchShift || 0, noteDurSec, pat.adsr, pat.loopStart, pat.loopEnd);
                  } else if (pat.adsr) {
                     scheduleOfflineSampleWithADSR(offCtx, buffer, offTrack.gainNode, noteSec, velocity, playbackRate, track.pitchShift || 0, 0, noteDurSec, pat.adsr);
                  } else {
                     scheduleOfflineSample(offCtx, buffer, offTrack.gainNode, noteSec, velocity, playbackRate, track.pitchShift || 0, 0, noteDurSec);
                  }
               }
            }
            scheduledCount++;
            continue;
         }

         const eventBuffer = event.buffer || track.buffer;
         if (!eventBuffer) continue;

         let evStartBeat = event.startBeat;
         let evEndBeat = event.startBeat + event.lengthBeats;

         // Clamp to render range
         if (evEndBeat <= startBeat || evStartBeat >= endBeat) continue;
         if (evStartBeat < startBeat) evStartBeat = startBeat;
         if (evEndBeat > endBeat) evEndBeat = endBeat;

         const eventStartSec = beatsToSeconds(evStartBeat - startBeat, bpm);
         const eventEndSec = beatsToSeconds(evEndBeat - startBeat, bpm);
         const eventDurationSec = eventEndSec - eventStartSec;
         if (eventDurationSec <= 0) continue;

         const bufferDuration = eventBuffer.duration;
         if (!bufferDuration || !isFinite(bufferDuration) || bufferDuration <= 0) continue;

         // Stretch ratio: only stretch when event.stretched is true
         // When false, play at natural speed (buffer loops/trims to fill event)
         const isStretched = event.stretched === true;
         const stretchRatio = isStretched ? (eventDurationSec / bufferDuration) : 1.0;
         const actualStretchRatio = Math.max(0.25, Math.min(4.0, stretchRatio));

         let playBuffer = eventBuffer;
         if (isStretched && (actualStretchRatio <= 0.97 || actualStretchRatio >= 1.03)) {
            try {
               playBuffer = stretchWithSoundTouch(offCtx, eventBuffer, actualStretchRatio);
            } catch (e) {
               console.warn('[render] stretch failed:', e);
               playBuffer = eventBuffer;
            }
         }

         const playBufferDuration = playBuffer.duration;

         // Per-event gain nodes
         const eventVol = event.volume !== undefined ? event.volume : 1.0;
         const fadeInGain = offCtx.createGain();
         const fadeOutGain = offCtx.createGain();
         fadeInGain.connect(fadeOutGain);
         fadeOutGain.connect(offTrack.gainNode);

         fadeOutGain.gain.value = eventVol;

         // Fade in
         const fadeInBeats = event.fadeInBeats || 0;
         const fadeOutBeats = event.fadeOutBeats || 0;
         let fadeInSec = beatsToSeconds(fadeInBeats, bpm);
         let fadeOutSec = beatsToSeconds(fadeOutBeats, bpm);
         if (fadeInSec + fadeOutSec > eventDurationSec) {
            const total = fadeInSec + fadeOutSec;
            if (total > 0) {
               fadeInSec = eventDurationSec * (fadeInSec / total);
               fadeOutSec = eventDurationSec * (fadeOutSec / total);
            }
         }

         if (fadeInSec > 0.001) {
            fadeInGain.gain.setValueAtTime(0.0001, eventStartSec);
            fadeInGain.gain.linearRampToValueAtTime(1, eventStartSec + fadeInSec);
         } else {
            fadeInGain.gain.value = 1;
         }

         if (fadeOutSec > 0.001) {
            const fadeOutStart = eventEndSec - fadeOutSec;
            fadeOutGain.gain.setValueAtTime(eventVol, fadeOutStart);
            fadeOutGain.gain.linearRampToValueAtTime(0.0001, eventEndSec);
         }

         // Source
         const source = offCtx.createBufferSource();
         source.buffer = playBuffer;
         source.playbackRate.value = 1.0;
         source.detune.value = (track.pitchShift || 0) * 100;
         source.loop = true;
         source.loopStart = 0;
         source.loopEnd = event._bakedAudioEndSec || playBufferDuration;

         // No sourceGain fade-in needed for offline render (always starts at buffer beginning)
         source.connect(fadeInGain);

         const bufferOffset = 0; // Always start from beginning for offline render
         try {
            source.start(eventStartSec, bufferOffset, eventDurationSec);
         } catch (e) {
            console.warn('[render] source.start failed:', e);
         }

         scheduledCount++;
      }
   }

   onProgress(0.20);

   // ---- Apply automations ----
   // Collect all automation segments from both the Map and timeline autoTracks
   const autoSegments = [];
   if (automation && automation.automationClips) {
      for (const [clipKey, clip] of automation.automationClips) {
         autoSegments.push(clip);
      }
   }
   // Also collect from timeline autoTracks (per-segment automation)
   if (opts.timeline && opts.timeline.state && opts.timeline.state.autoTracks) {
      for (const seg of opts.timeline.state.autoTracks) {
         autoSegments.push(seg);
      }
   }

   for (const clip of autoSegments) {
         const paramName = clip.paramName.toLowerCase();
         const steps = Math.max(2, Math.ceil(totalBeats * 16)); // 16 steps per beat for smooth curves

         // Determine target node(s) on the offline context
         let targetNodes = []; // array of { node, param, transform }
         // transform: function(val) -> audioParam value

         if (clip.trackId === 'master' || paramName.startsWith('master')) {
            // Master params
            if (paramName === 'master volume' || paramName === 'master vol') {
               targetNodes.push({ node: masterGain.gain, transform: v => v });
            } else if (paramName === 'master pan') {
               targetNodes.push({ node: masterPan.pan, transform: v => v / 50 });
            } else if (paramName === 'master eq low' || paramName === 'master eq lo') {
               targetNodes.push({ node: masterEqLow.gain, transform: v => v });
            } else if (paramName === 'master eq mid') {
               targetNodes.push({ node: masterEqMid.gain, transform: v => v });
            } else if (paramName === 'master eq high' || paramName === 'master eq hi') {
               targetNodes.push({ node: masterEqHi.gain, transform: v => v });
            }
         } else if (clip.isKnob || (clip.trackId && String(clip.trackId).startsWith('knob_'))) {
            // Standalone knob/fader automation — match by paramName
            // The trackId is synthetic (e.g. "knob_PAN"), so we need to find
            // the actual target by matching the paramName to known mixer params
            const pn = paramName;
            // Try to extract the real track ID from the knobId (e.g. "mixer_5_pan" -> "5")
            const knobId = clip.knobId || '';
            // knobId patterns: mixer_<trackId>_<param>, mixer_master_<param>
            const mixerMatch = knobId.match(/^mixer_(.+)_([a-z_]+)$/);
            if (mixerMatch) {
               const realTrackId = mixerMatch[1];
               const realParam = mixerMatch[2];
               if (realTrackId === 'master') {
                  if (realParam === 'vol') targetNodes.push({ node: masterGain.gain, transform: v => v });
                  else if (realParam === 'pan') targetNodes.push({ node: masterPan.pan, transform: v => v / 50 });
                  else if (realParam === 'eq_low') targetNodes.push({ node: masterEqLow.gain, transform: v => v });
                  else if (realParam === 'eq_mid') targetNodes.push({ node: masterEqMid.gain, transform: v => v });
                  else if (realParam === 'eq_hi') targetNodes.push({ node: masterEqHi.gain, transform: v => v });
                  else if (realParam === 'comp_threshold') targetNodes.push({ node: masterComp.threshold, transform: v => v });
                  else if (realParam === 'comp_ratio') targetNodes.push({ node: masterComp.ratio, transform: v => v });
                  else if (realParam === 'comp_knee') targetNodes.push({ node: masterComp.knee, transform: v => v });
                  else if (realParam === 'comp_attack') targetNodes.push({ node: masterComp.attack, transform: v => v });
                  else if (realParam === 'comp_release') targetNodes.push({ node: masterComp.release, transform: v => v });
               } else {
                  const offTrack = offTracks[realTrackId];
                  if (offTrack) {
                     if (realParam === 'vol') targetNodes.push({ node: offTrack.mixerGain.gain, transform: v => v });
                     else if (realParam === 'pan') targetNodes.push({ node: offTrack.mixerPan.pan, transform: v => v / 50 });
                     else if (realParam === 'eq_low') targetNodes.push({ node: offTrack.eqLow.gain, transform: v => v });
                     else if (realParam === 'eq_mid') targetNodes.push({ node: offTrack.eqMid.gain, transform: v => v });
                     else if (realParam === 'eq_hi') targetNodes.push({ node: offTrack.eqHi.gain, transform: v => v });
                  }
               }
            }
         } else {
            const offTrack = offTracks[clip.trackId];
            if (offTrack) {
               if ((paramName === 'volume' || paramName === 'vol') && !paramName.includes('mixer')) {
                  targetNodes.push({ node: offTrack.gainNode.gain, transform: v => v });
               } else if (paramName === 'mixer volume' || paramName === 'mixer vol' || paramName === 'mixervolume') {
                  targetNodes.push({ node: offTrack.mixerGain.gain, transform: v => v });
               } else if (paramName === 'pan' && !paramName.includes('mixer')) {
                  targetNodes.push({ node: offTrack.pannerNode.pan, transform: v => v / 50 });
               } else if (paramName === 'mixer pan' || paramName === 'mixerpan') {
                  targetNodes.push({ node: offTrack.mixerPan.pan, transform: v => v / 50 });
               } else if (paramName === 'eq low' || paramName === 'eq lo' || paramName === 'eq-low') {
                  targetNodes.push({ node: offTrack.eqLow.gain, transform: v => v });
               } else if (paramName === 'eq mid') {
                  targetNodes.push({ node: offTrack.eqMid.gain, transform: v => v });
               } else if (paramName === 'eq high' || paramName === 'eq hi' || paramName === 'eq-high') {
                  targetNodes.push({ node: offTrack.eqHi.gain, transform: v => v });
               } else {
                  // FX parameter automation: find the offline FX that has this param
                  // The clip.paramName matches an input name on one of the FX
                  // The clip.inputEl.dataset.label or clip.paramName identifies the param
                  const fxParamName = clip.paramName;
                  // Also check the input element's dataset for the FX-specific label
                  const inputLabel = clip.inputEl ? (clip.inputEl.dataset && clip.inputEl.dataset.label) : null;
                  for (const offFx of (offTrack.fxNodes || [])) {
                     if (offFx.params) {
                        // Try matching by paramName or inputLabel
                        const paramSpec = offFx.params[fxParamName] || (inputLabel ? offFx.params[inputLabel] : null);
                        if (paramSpec) {
                           const targets = Array.isArray(paramSpec) ? paramSpec : [paramSpec];
                           for (const t of targets) {
                              targetNodes.push({ node: t.node, transform: t.transform });
                           }
                           break; // Found the FX with this param
                        }
                     }
                  }
               }
            }
         }

         for (const target of targetNodes) {
            for (let i = 0; i < steps; i++) {
               const normPos = i / (steps - 1);
               const beat = clip.startBeat + normPos * clip.lengthBeats;
               if (beat < startBeat || beat > endBeat) continue;
               const timeSec = beatsToSeconds(beat - startBeat, bpm);
               // Evaluate automation value: check Map clip or timeline segment directly
               let val = null;
               if (automation && automation.getAutomationValueAt) {
                  val = automation.getAutomationValueAt(clip.trackId, clip.paramName, beat);
               }
               if (val === null && clip.points) {
                  // Timeline segment: evaluate directly
                  const segNormPos = (beat - (clip.startBeat || 0)) / (clip.lengthBeats || 4);
                  if (segNormPos >= 0 && segNormPos <= 1) {
                     val = evalSegment(clip, segNormPos);
                  }
               }
               if (val !== null && timeSec >= 0 && timeSec < totalSeconds) {
                  try {
                     target.node.setValueAtTime(target.transform(val), timeSec);
                  } catch (e) {}
               }
            }
         }
      }

   onProgress(0.30);

   // ---- Wait for async FX initialization ----
   // AudioWorklet-based FX load their processor modules via async addModule().
   // We must await their `ready` promises before rendering, otherwise the
   // worklet nodes won't be connected and the FX produce silence.
   // Also yield to the event loop for room/reverb impulse response fetches.
   if (fxReadyPromises.length > 0) {
      await Promise.all(fxReadyPromises).catch(e => console.warn('[render] FX ready error:', e));
   }
   await new Promise(r => setTimeout(r, 300));

   // ---- Render ----
   try {
      const renderedBuffer = await offCtx.startRendering();
      onProgress(0.80);

      // Trim leading silence caused by ScriptProcessorNode FX latency
      const trimmedBuffer = trimLeadingSilence(renderedBuffer, offCtx);

      // Encode to WAV
      const wavBlob = audioBufferToWav(trimmedBuffer);
      onProgress(1.0);
      return wavBlob;
   } catch (e) {
      console.error('[render] offline render failed:', e);
      throw e;
   }
}

// ---- Load MIDI sample (cached) ----
const offlineSampleCache = new Map();
let offlineSampleLoader = null;

function setOfflineSampleLoader(loader) {
   offlineSampleLoader = loader;
}

async function loadMIDISample(sampleId) {
   if (!sampleId) return null;
   if (offlineSampleCache.has(sampleId)) return offlineSampleCache.get(sampleId);
   if (!offlineSampleLoader) return null;
   const buf = await offlineSampleLoader(sampleId);
   if (buf) offlineSampleCache.set(sampleId, buf);
   return buf;
}

// ---- Schedule a sample in the offline context ----
function scheduleOfflineSample(offCtx, buffer, destNode, whenSec, volume, playbackRate, pitchShiftSemitones, offset, duration) {
   scheduleOfflineSampleWithADSR(offCtx, buffer, destNode, whenSec, volume, playbackRate, pitchShiftSemitones, offset, duration, null);
}

function scheduleOfflineSampleWithADSR(offCtx, buffer, destNode, whenSec, volume, playbackRate, pitchShiftSemitones, offset, duration, adsr) {
   try {
      const src = offCtx.createBufferSource();
      src.buffer = buffer;
      src.playbackRate.value = playbackRate;
      src.detune.value = (pitchShiftSemitones || 0) * 100;

      const gain = offCtx.createGain();

      if (adsr) {
         const a = Math.max(0, adsr.attack || 0);
         const d = Math.max(0, adsr.decay || 0);
         const s = Math.max(0, Math.min(1, adsr.sustain !== undefined ? adsr.sustain : 1));
         const r = Math.max(0, adsr.release || 0);
         const dur = duration || (buffer.duration / playbackRate);
         const noteOffTime = whenSec + dur;

         gain.gain.setValueAtTime(0, whenSec);
         if (a > 0) {
            gain.gain.linearRampToValueAtTime(volume, whenSec + a);
         } else {
            gain.gain.setValueAtTime(volume, whenSec);
         }
         if (d > 0) {
            gain.gain.linearRampToValueAtTime(volume * s, whenSec + a + d);
         } else {
            gain.gain.setValueAtTime(volume * s, whenSec + a);
         }
         if (r > 0) {
            gain.gain.setValueAtTime(volume * s, noteOffTime);
            gain.gain.linearRampToValueAtTime(0, noteOffTime + r);
         } else {
            gain.gain.setValueAtTime(0, noteOffTime);
         }

         src.connect(gain);
         gain.connect(destNode);
         const startOffset = offset || 0;
         const totalDur = dur + r;
         src.start(whenSec, startOffset, totalDur / playbackRate);
      } else {
         const fadeSec = 0.003;
         gain.gain.setValueAtTime(0, whenSec);
         gain.gain.linearRampToValueAtTime(volume, whenSec + fadeSec);
         if (duration && duration > fadeSec * 2) {
            gain.gain.setValueAtTime(volume, whenSec + duration - fadeSec);
            gain.gain.linearRampToValueAtTime(0, whenSec + duration);
         } else {
            gain.gain.linearRampToValueAtTime(0, whenSec + fadeSec + 0.05);
         }
         src.connect(gain);
         gain.connect(destNode);
         const startOffset = offset || 0;
         if (duration) {
            src.start(whenSec, startOffset, duration / playbackRate);
         } else {
            src.start(whenSec, startOffset);
         }
      }
   } catch (e) {
      console.warn('[render] scheduleOfflineSample failed:', e);
   }
}

function scheduleOfflineSampleLooped(offCtx, buffer, destNode, whenSec, volume, playbackRate, pitchShiftSemitones, duration, adsr, loopStart, loopEnd) {
   try {
      const src = offCtx.createBufferSource();
      src.buffer = buffer;
      src.playbackRate.value = playbackRate;
      src.detune.value = (pitchShiftSemitones || 0) * 100;

      // Set up loop
      if (loopStart >= 0 && loopEnd > loopStart && loopEnd <= buffer.length) {
         src.loop = true;
         src.loopStart = loopStart / buffer.sampleRate;
         src.loopEnd = loopEnd / buffer.sampleRate;
      }

      const gain = offCtx.createGain();
      const peak = volume;

      if (adsr) {
         const a = Math.max(0, adsr.attack || 0);
         const d = Math.max(0, adsr.decay || 0);
         const s = Math.max(0, Math.min(1, adsr.sustain !== undefined ? adsr.sustain : 1));
         const r = Math.max(0, adsr.release || 0);
         const dur = duration || (buffer.duration / playbackRate);
         const noteOffTime = whenSec + dur;

         gain.gain.setValueAtTime(0, whenSec);
         if (a > 0) gain.gain.linearRampToValueAtTime(peak, whenSec + a);
         else gain.gain.setValueAtTime(peak, whenSec);
         if (d > 0) gain.gain.linearRampToValueAtTime(peak * s, whenSec + a + d);
         else gain.gain.setValueAtTime(peak * s, whenSec + a);
         if (r > 0) {
            gain.gain.setValueAtTime(peak * s, noteOffTime);
            gain.gain.linearRampToValueAtTime(0, noteOffTime + r);
         } else {
            gain.gain.setValueAtTime(0, noteOffTime);
         }
      } else {
         const fadeSec = 0.003;
         gain.gain.setValueAtTime(0, whenSec);
         gain.gain.linearRampToValueAtTime(peak, whenSec + fadeSec);
         if (duration && duration > fadeSec * 2) {
            gain.gain.setValueAtTime(peak, whenSec + duration - fadeSec);
            gain.gain.linearRampToValueAtTime(0, whenSec + duration);
         } else {
            gain.gain.linearRampToValueAtTime(0, whenSec + fadeSec + 0.05);
         }
      }

      src.connect(gain);
      gain.connect(destNode);

      const totalDur = duration + (adsr ? (adsr.release || 0) : 0);
      src.start(whenSec, 0, totalDur / playbackRate);
   } catch (e) {
      console.warn('[render] scheduleOfflineSampleLooped failed:', e);
   }
}

// ---- Evaluate automation segment at a normalized position ----
function evalSegment(clip, normPos) {
   if (!clip || !clip.points || clip.points.length === 0) return null;
   const pts = clip.points;
   if (pts.length === 1) return pts[0].y;
   if (normPos <= pts[0].x) return pts[0].y;
   if (normPos >= pts[pts.length - 1].x) return pts[pts.length - 1].y;
   // Linear interpolation (good enough for offline render)
   for (let i = 0; i < pts.length - 1; i++) {
      if (normPos >= pts[i].x && normPos <= pts[i + 1].x) {
         const t = (normPos - pts[i].x) / (pts[i + 1].x - pts[i].x);
         return pts[i].y + (pts[i + 1].y - pts[i].y) * t;
      }
   }
   return pts[pts.length - 1].y;
}

// ---- SoundTouch stretch helper (works on offline context) ----
function stretchWithSoundTouch(offCtx, inputBuffer, stretchRatio) {
   if (!SoundTouch || !SimpleFilter || !WebAudioBufferSource) {
      throw new Error('SoundTouch not available');
   }

   const source = new WebAudioBufferSource(inputBuffer);
   const soundTouch = new SoundTouch();
   soundTouch.tempo = 1 / stretchRatio;
   soundTouch.pitch = 1.0;

   const filter = new SimpleFilter(source, soundTouch);
   const sampleRate = inputBuffer.sampleRate;
   const numChannels = inputBuffer.numberOfChannels;
   const estimatedOutputFrames = Math.floor(inputBuffer.length * stretchRatio + 1024);

   const chunkSize = 4096;
   const allSamples = [];
   let totalFrames = 0;

   while (true) {
      const samples = new Float32Array(chunkSize * 2);
      const extracted = filter.extract(samples, chunkSize);
      if (extracted === 0) break;
      if (extracted < chunkSize) {
         allSamples.push(samples.slice(0, extracted * 2));
      } else {
         allSamples.push(samples);
      }
      totalFrames += extracted;
   }

   if (totalFrames === 0) return inputBuffer;

   const outputBuffer = offCtx.createBuffer(numChannels, totalFrames, sampleRate);
   const outLeft = outputBuffer.getChannelData(0);
   const outRight = numChannels > 1 ? outputBuffer.getChannelData(1) : outputBuffer.getChannelData(0);

   let frameIdx = 0;
   for (const chunk of allSamples) {
      const chunkFrames = chunk.length / 2;
      for (let i = 0; i < chunkFrames && frameIdx < totalFrames; i++) {
         outLeft[frameIdx] = chunk[i * 2];
         outRight[frameIdx] = chunk[i * 2 + 1];
         frameIdx++;
      }
   }

   return outputBuffer;
}

module.exports = { renderToWav, setOfflineSampleLoader, audioBufferToWav };
